import { X, Search, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

// Modal
export const Modal = ({ open, onClose, title, children, size = '', footer }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${size ? 'modal-' + size : ''} slide-up`}>
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        {children}
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

// Badge
export const Badge = ({ type = 'neutral', children }) => (
  <span className={`badge badge-${type}`}>{children}</span>
);

// Status badge
export const StatusBadge = ({ status }) => {
  const map = {
    present: 'success', absent: 'danger', late: 'warning', half_day: 'warning',
    paid: 'success', pending: 'warning', overdue: 'danger', waived: 'neutral',
    active: 'success', inactive: 'danger',
    issued: 'info', returned: 'success',
    sent: 'success', failed: 'danger',
    A: 'success', B: 'info', C: 'warning', D: 'danger', F: 'danger',
  };
  return <Badge type={map[status] || 'neutral'}>{status?.replace('_', ' ')}</Badge>;
};

// Avatar
export const Avatar = ({ src, name, size = '' }) => {
  const initials = name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  return src
    ? <img src={src} alt={name} className={`avatar ${size}`} />
    : <div className={`avatar ${size}`}>{initials}</div>;
};

// Search bar
export const SearchBar = ({ value, onChange, placeholder = 'Search...' }) => (
  <div className="search-bar">
    <Search size={15} />
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

// Table
export const Table = ({ columns, data, loading, emptyMsg = 'No data found' }) => {
  if (loading) return <div className="loading-screen"><div className="spinner spinner-dark" /><span>Loading...</span></div>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>{columns.map(c => <th key={c.key || c.label}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {!data?.length
            ? <tr><td colSpan={columns.length}><div className="empty-state"><AlertCircle size={36} /><p>{emptyMsg}</p></div></td></tr>
            : data.map((row, i) => (
              <tr key={i}>{columns.map(c => <td key={c.key || c.label}>{c.render ? c.render(row) : row[c.key]}</td>)}</tr>
            ))
          }
        </tbody>
      </table>
    </div>
  );
};

// Pagination
export const Pagination = ({ page, total, limit, onPage }) => {
  const pages = Math.ceil(total / limit);
  if (pages <= 1) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 16 }}>
      <button className="btn btn-secondary btn-sm btn-icon" onClick={() => onPage(page - 1)} disabled={page === 1}><ChevronLeft size={15} /></button>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Page {page} of {pages}</span>
      <button className="btn btn-secondary btn-sm btn-icon" onClick={() => onPage(page + 1)} disabled={page === pages}><ChevronRight size={15} /></button>
    </div>
  );
};

// FormField
export const FormField = ({ label, required, children, error }) => (
  <div className="form-group">
    {label && <label className="form-label">{label}{required && <span style={{ color: 'var(--danger)' }}> *</span>}</label>}
    {children}
    {error && <span style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>{error}</span>}
  </div>
);

// Select
export const Select = ({ value, onChange, options = [], placeholder = 'Select...', className = '' }) => (
  <select className={`form-control ${className}`} value={value} onChange={e => onChange(e.target.value)}>
    <option value="">{placeholder}</option>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

// Stat Card
export const StatCard = ({ icon, label, value, sub, color = 'blue' }) => (
  <div className={`stat-card ${color}`}>
    <div className={`stat-icon ${color}`}>{icon}</div>
    <div className="stat-info">
      <div className="stat-value">{value ?? '—'}</div>
      <div className="stat-label">{label}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  </div>
);

// Page Header
export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="page-header">
    <div>
      <h1 className="page-title">{title}</h1>
      {subtitle && <p className="page-subtitle">{subtitle}</p>}
    </div>
    {actions && <div className="page-actions">{actions}</div>}
  </div>
);

// Confirm Delete Dialog
export const ConfirmDialog = ({ open, onClose, onConfirm, title, message, loading }) => (
  <Modal open={open} onClose={onClose} title={title || 'Confirm Action'} size="sm"
    footer={<>
      <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      <button className="btn btn-danger" onClick={onConfirm} disabled={loading}>
        {loading ? <><span className="spinner" /> Deleting...</> : 'Delete'}
      </button>
    </>}>
    <p style={{ color: 'var(--text-secondary)' }}>{message || 'Are you sure? This action cannot be undone.'}</p>
  </Modal>
);

// Tabs
export const Tabs = ({ tabs, active, onChange }) => (
  <div className="tabs">
    {tabs.map(t => (
      <button key={t.key} className={`tab-btn ${active === t.key ? 'active' : ''}`} onClick={() => onChange(t.key)}>
        {t.icon && t.icon} {t.label}
      </button>
    ))}
  </div>
);

// Info Row (for profile views)
export const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ fontWeight: 700, minWidth: 160, fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{label}</span>
    <span style={{ fontSize: '0.9rem' }}>{value || '—'}</span>
  </div>
);
